import { type ReactNode, useEffect, useRef, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Box,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Building2 } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { ListingActivityContext } from '@modules/_shared/components/feed/types';
import { SearchInput } from '@modules/_shared/components/search-input/SearchInput';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { getUserDisplayName } from '@modules/_shared/components/UserCard';
import { useLocale } from '@shared/hooks/locale';

const GET_LISTING_ACTIVITY_CONTEXT = gql(/* GraphQL */ `
  query ListingActivityContext($lotId: uuid!) {
    buyer_leads(
      where: { lot_id: { _eq: $lotId } }
      order_by: { updated_at: desc }
      limit: 100
    ) {
      id
      user {
        ...UserAvatar
        ...UserCardDisplayName
      }
      lead_stage {
        id
        label
      }
    }
    lots_by_pk(id: $lotId) {
      id
      sellers {
        seller {
          ...UserAvatar
          ...UserCardDisplayName
        }
      }
    }
  }
`);

const LIST_MIN_HEIGHT = 300;
const LIST_MAX_HEIGHT = 420;

type TabValue = 'buyer' | 'seller' | 'listing';

type ContextOption = {
  key: string;
  primary: string;
  secondary?: string;
  avatar: ReactNode;
  onSelect: () => void;
};

type ListingActivityContextDialogProps = {
  open: boolean;
  lotId: string;
  onClose: () => void;
  onSelect: (context: ListingActivityContext) => void;
};

export const ListingActivityContextDialog = ({
  open,
  lotId,
  onClose,
  onSelect,
}: ListingActivityContextDialogProps) => {
  const { t } = useLocale();
  const [selectedTab, setSelectedTab] = useState<TabValue | null>(null);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const highlightedRef = useRef<HTMLDivElement | null>(null);
  const keyboardNavRef = useRef(false);

  const { data, loading, error } = useQuery(GET_LISTING_ACTIVITY_CONTEXT, {
    variables: { lotId },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (open) {
      setSelectedTab(null);
      setSearch('');
      setHighlightedIndex(0);
    }
  }, [open]);

  const buyerLeads = (data?.buyer_leads ?? []).filter(
    (bl): bl is typeof bl & { user: NonNullable<typeof bl.user> } =>
      bl.user != null,
  );
  const sellers = (data?.lots_by_pk?.sellers ?? [])
    .map(entry => entry.seller)
    .filter((seller): seller is NonNullable<typeof seller> => seller != null);

  const selectGeneral = () => {
    onSelect({
      parentType: 'Lot',
      parentId: lotId,
      contactIds: [],
      contextLotId: null,
    });
  };

  const shouldAutoSkip =
    open &&
    !loading &&
    !error &&
    buyerLeads.length === 0 &&
    sellers.length === 0;

  useEffect(() => {
    if (shouldAutoSkip) {
      selectGeneral();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoSkip]);

  const defaultTab: TabValue =
    buyerLeads.length > 0 ? 'buyer' : sellers.length > 0 ? 'seller' : 'listing';
  const activeTab: TabValue =
    selectedTab != null &&
    (selectedTab === 'listing' ||
      (selectedTab === 'buyer' && buyerLeads.length > 0) ||
      (selectedTab === 'seller' && sellers.length > 0))
      ? selectedTab
      : defaultTab;

  const query = search.trim().toLowerCase();
  const matchesQuery = (name?: string | null) =>
    query === '' || (name ?? '').toLowerCase().includes(query);

  const searchText = (user: {
    first_name?: string | null;
    last_name?: string | null;
    full_name?: string | null;
    emails: ReadonlyArray<{ email: string }>;
  }) =>
    [user.first_name, user.last_name, user.full_name, user.emails[0]?.email]
      .filter(Boolean)
      .join(' ');

  const buyerOptions: ContextOption[] = buyerLeads
    .filter(bl => matchesQuery(searchText(bl.user)))
    .map(buyerLead => ({
      key: buyerLead.id,
      primary: getUserDisplayName(buyerLead.user),
      secondary: buyerLead.lead_stage?.label ?? undefined,
      avatar: <UserAvatar user={buyerLead.user} size={36} />,
      onSelect: () =>
        onSelect({
          parentType: 'BuyerLead',
          parentId: buyerLead.id,
          contactIds: [buyerLead.user.id],
          contextLotId: lotId,
        }),
    }));

  const sellerOptions: ContextOption[] = sellers
    .filter(seller => matchesQuery(searchText(seller)))
    .map(seller => ({
      key: seller.id,
      primary: getUserDisplayName(seller),
      avatar: <UserAvatar user={seller} size={36} />,
      onSelect: () =>
        onSelect({
          parentType: 'Lot',
          parentId: lotId,
          contactIds: [seller.id],
          contextLotId: null,
        }),
    }));

  const listingOption: ContextOption = {
    key: 'general',
    primary: t('General (no contact)'),
    secondary: t('Filed on the listing only'),
    avatar: (
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          bgcolor: 'grey.100',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Building2 size={18} />
      </Box>
    ),
    onSelect: selectGeneral,
  };

  const activeOptions: ContextOption[] =
    activeTab === 'buyer'
      ? buyerOptions
      : activeTab === 'seller'
      ? sellerOptions
      : [listingOption];

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search, activeTab]);

  useEffect(() => {
    if (keyboardNavRef.current) {
      highlightedRef.current?.scrollIntoView({ block: 'nearest' });
      keyboardNavRef.current = false;
    }
  }, [highlightedIndex, activeTab]);

  const handleTabChange = (_: React.SyntheticEvent, value: TabValue) => {
    setSelectedTab(value);
    setSearch('');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (activeOptions.length === 0) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      keyboardNavRef.current = true;
      setHighlightedIndex(i => Math.min(i + 1, activeOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      keyboardNavRef.current = true;
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activeOptions[highlightedIndex]?.onSelect();
    }
  };

  const isLoadingInitial =
    loading && buyerLeads.length === 0 && sellers.length === 0;

  if (shouldAutoSkip || isLoadingInitial) {
    return null;
  }

  return (
    <SlideDialog
      open={open}
      onClose={onClose}
      title={t('Who is this activity about?')}
      footerActions={<></>}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: theme =>
              `1px solid ${(theme.vars ?? theme).palette.divider}`,
          }}
        >
          {buyerLeads.length > 0 && (
            <Tab value="buyer" label={t('Buyer leads')} />
          )}
          {sellers.length > 0 && (
            <Tab
              value="seller"
              label={sellers.length > 1 ? t('Sellers') : t('Seller')}
            />
          )}
          <Tab value="listing" label={t('Listing')} />
        </Tabs>

        {activeTab !== 'listing' && (
          <Box sx={{ p: 1.5, pb: 1 }}>
            <SearchInput
              fullWidth
              autoFocus
              value={search}
              debounceValueChanges={false}
              onValueChanged={setSearch}
              onKeyDown={handleSearchKeyDown}
              placeholder={t('Search')}
            />
          </Box>
        )}

        <Box
          sx={{
            minHeight: LIST_MIN_HEIGHT,
            maxHeight: LIST_MAX_HEIGHT,
            overflowY: 'auto',
          }}
        >
          {activeOptions.length === 0 ? (
            <EmptyState label={t('No results')} />
          ) : (
            <List disablePadding sx={{ pb: 1 }}>
              {activeOptions.map((option, index) => (
                <ListItemButton
                  key={option.key}
                  ref={index === highlightedIndex ? highlightedRef : undefined}
                  selected={index === highlightedIndex}
                  onMouseEnter={() => {
                    keyboardNavRef.current = false;
                    setHighlightedIndex(index);
                  }}
                  onClick={option.onSelect}
                >
                  <ListItemAvatar>{option.avatar}</ListItemAvatar>
                  <ListItemText
                    primary={option.primary}
                    secondary={option.secondary}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </SlideDialog>
  );
};

const EmptyState = ({ label }: { label: string }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);
